/**
 * RecurrenteConfigModal
 *
 * Modal de configuración para convertir una FE (en CrearFactura) en una
 * plantilla recurrente. La plantilla se crea solo cuando la FE es aceptada
 * por Hacienda (la config se guarda en metadata.pendingRecurring; el
 * statusChecker la dispara al pasar a 'aceptado').
 *
 * Props:
 *   initialConfig: config previa (si el usuario ya abrió el modal y vuelve).
 *   onClose:  () => void
 *   onConfirm: (config) => void  // se llama con la config para guardar en el form padre
 *   defaultNombre: string  // sugerencia (ej: nombre del cliente)
 */

import { useState } from 'react';
import { X, Repeat, Calendar, Info, FileText, Zap } from 'lucide-react';
import dayjs from 'dayjs';

const FRECUENCIAS = [
  { value: 'weekly',    label: 'Semanal',    desc: 'Cada semana' },
  { value: 'biweekly',  label: 'Quincenal',  desc: 'Cada 15 días' },
  { value: 'monthly',   label: 'Mensual',    desc: 'Cada mes' },
  { value: 'quarterly', label: 'Trimestral', desc: 'Cada 3 meses' },
  { value: 'yearly',    label: 'Anual',      desc: 'Cada año' }
];

const DIAS_MES = Array.from({ length: 28 }, (_, i) => i + 1);

export default function RecurrenteConfigModal({ initialConfig, onClose, onConfirm, defaultNombre = '' }) {
  const [frecuencia, setFrecuencia] = useState(initialConfig?.frecuencia || 'monthly');
  const [diasGeneracion, setDiasGeneracion] = useState(initialConfig?.diasGeneracion || [1]);
  const [proximaGeneracion, setProximaGeneracion] = useState(
    initialConfig?.proximaGeneracion || dayjs().add(1, 'month').startOf('month').format('YYYY-MM-DD')
  );
  const [generaComo, setGeneraComo] = useState(initialConfig?.generaComo || 'draft');
  const [nombre, setNombre] = useState(initialConfig?.nombre || (defaultNombre ? `Mensualidad - ${defaultNombre}` : ''));

  const requiereDias = ['monthly', 'quarterly', 'yearly'].includes(frecuencia);

  const toggleDia = (d) => {
    setDiasGeneracion(prev => {
      const next = prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort((a, b) => a - b);
      return next.length > 0 ? next : [d]; // al menos 1
    });
  };

  const handleConfirm = () => {
    onConfirm({
      frecuencia,
      diasGeneracion: requiereDias ? diasGeneracion : [],
      proximaGeneracion,
      generaComo,
      nombre: nombre.trim() || null
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-cyan-600 to-blue-700 rounded-t-xl shrink-0">
          <h3 className="font-semibold text-white text-lg flex items-center gap-2">
            <Repeat className="h-5 w-5" />
            Convertir en Plantilla Recurrente
          </h3>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Frecuencia */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-cyan-600" /> Frecuencia
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {FRECUENCIAS.map(f => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFrecuencia(f.value)}
                  className={`px-3 py-3 rounded-lg border-2 text-center transition-all ${
                    frecuencia === f.value
                      ? 'border-cyan-500 bg-cyan-50 text-cyan-700 shadow-sm'
                      : 'border-gray-200 text-gray-600 hover:border-cyan-300 hover:bg-cyan-50/50'
                  }`}
                >
                  <div className="text-sm font-semibold">{f.label}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">{f.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Días del mes (solo para Mensual/Trimestral/Anual) */}
          {requiereDias && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Día(s) del mes en que se genera
                <span className="ml-2 text-xs font-normal text-gray-500">
                  ({diasGeneracion.length} día{diasGeneracion.length !== 1 ? 's' : ''} seleccionado{diasGeneracion.length !== 1 ? 's' : ''})
                </span>
              </label>
              <div className="grid grid-cols-7 sm:grid-cols-14 gap-1.5">
                {DIAS_MES.map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDia(d)}
                    className={`h-9 rounded-md border text-sm font-medium transition-all ${
                      diasGeneracion.includes(d)
                        ? 'border-cyan-500 bg-cyan-500 text-white shadow-sm'
                        : 'border-gray-200 text-gray-600 hover:border-cyan-300 hover:bg-cyan-50'
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5">
                Hacienda admite hasta el día 28 (para evitar problemas con meses de 30/31 días o febrero).
              </p>
            </div>
          )}

          {/* Próxima generación */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Próxima generación
            </label>
            <input
              type="date"
              value={proximaGeneracion}
              onChange={(e) => setProximaGeneracion(e.target.value)}
              min={dayjs().format('YYYY-MM-DD')}
              className="w-full md:w-auto border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-400"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              A partir de esta fecha, el sistema generará una factura automáticamente en cada ciclo.
            </p>
          </div>

          {/* Modo de generación */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Modo de generación
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setGeneraComo('draft')}
                className={`p-3 rounded-lg border-2 text-left transition-all flex items-start gap-3 ${
                  generaComo === 'draft'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <FileText className={`h-5 w-5 mt-0.5 ${generaComo === 'draft' ? 'text-blue-600' : 'text-gray-400'}`} />
                <div className="flex-1">
                  <div className={`text-sm font-semibold ${generaComo === 'draft' ? 'text-blue-700' : 'text-gray-700'}`}>
                    Borrador
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">Revisar y aprobar antes de enviar</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setGeneraComo('pending')}
                className={`p-3 rounded-lg border-2 text-left transition-all flex items-start gap-3 ${
                  generaComo === 'pending'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <Zap className={`h-5 w-5 mt-0.5 ${generaComo === 'pending' ? 'text-green-600' : 'text-gray-400'}`} />
                <div className="flex-1">
                  <div className={`text-sm font-semibold ${generaComo === 'pending' ? 'text-green-700' : 'text-gray-700'}`}>
                    Pendiente
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">Envío automático a Hacienda en cada ciclo</div>
                </div>
              </button>
            </div>
          </div>

          {/* Nombre de la plantilla */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nombre de la plantilla <span className="text-xs font-normal text-gray-400">(opcional)</span>
            </label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder={defaultNombre ? `Mensualidad - ${defaultNombre}` : 'Ej: Mensualidad Coca-Cola - Plan Premium'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-400"
            />
          </div>

          {/* Aviso importante */}
          <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border-2 border-amber-200 rounded-lg">
            <Info className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <strong>Importante:</strong> la plantilla recurrente se creará automáticamente <strong>solo cuando esta factura sea aceptada por Hacienda</strong>. Si Hacienda la rechaza, no se creará. Esto asegura que la plantilla se base en datos válidos.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl shrink-0 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={requiereDias && diasGeneracion.length === 0}
            className="px-6 py-2 border-2 border-cyan-500 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 hover:border-cyan-600 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Repeat className="h-4 w-4" />
            Confirmar Recurrencia
          </button>
        </div>
      </div>
    </div>
  );
}
